import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate, NavLink } from 'react-router-dom';
import {
  Hand, Megaphone, HelpCircle, Link2, Trophy, ChevronRight, Image as ImageIcon,
  Heart, MessageCircle, Pin, CheckCircle, Trash2, Send, Loader2, X, ArrowLeft, ShieldAlert,
} from 'lucide-react';
import { portalApi, usePortalAuth, formatApiError } from '../../contexts/PortalAuthContext';
import { toast } from 'sonner';

const CHANNEL_ICONS = { wave: Hand, megaphone: Megaphone, help: HelpCircle, link: Link2, trophy: Trophy };
const ICON_OF = (slug) => {
  const map = { introductions: Hand, announcements: Megaphone, 'ask-jo': HelpCircle, 'show-tell': Link2, wins: Trophy };
  return map[slug] || MessageCircle;
};

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function Avatar({ user, size = 'md' }) {
  const sz = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';
  const src = user?.avatar_url
    ? (user.avatar_url.startsWith('http') ? user.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`)
    : null;
  if (src) return <img src={src} alt="" className={`${sz} rounded-full object-cover border border-slate-700 shrink-0`} />;
  return (
    <div className={`${sz} rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center font-semibold text-slate-300 shrink-0`}>
      {(user?.full_name || 'U').slice(0,1).toUpperCase()}
    </div>
  );
}

// =====================================================================
// CHANNEL LIST + FEED (default community page)
// =====================================================================
export default function PortalCommunity() {
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [rules, setRules] = useState([]);

  useEffect(() => {
    portalApi.get('/community/channels')
      .then(({ data }) => { setChannels(data.channels); setRules(data.rules); })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="portal-community">
      <header className="mb-8">
        <div className="text-amber-400 text-xs uppercase tracking-widest mb-2">Community</div>
        <h1 className="text-3xl font-semibold text-white">Cohort feed</h1>
        <p className="text-slate-400 mt-2">Channels for intros, questions, work, and wins. Pick one to dive in.</p>
      </header>

      {/* Channel grid */}
      <div className="grid sm:grid-cols-2 gap-3 mb-8">
        {channels.map(ch => {
          const Icon = ICON_OF(ch.slug);
          return (
            <button
              key={ch.slug}
              onClick={() => navigate(`/portal/community/${ch.slug}`)}
              className="group text-left rounded-xl border border-slate-800 bg-slate-900/60 hover:border-amber-500/40 hover:bg-slate-900 p-5 transition flex items-start gap-4"
              data-testid={`community-channel-${ch.slug}`}
            >
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-white font-semibold group-hover:text-amber-400 transition">{ch.name}</h3>
                  {ch.posts === 'admin' && <span className="text-[10px] uppercase bg-rose-500/20 text-rose-400 px-1.5 py-0.5 rounded">Jo only</span>}
                </div>
                <p className="text-sm text-slate-400 mt-1">{ch.description}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-amber-400 transition self-center" />
            </button>
          );
        })}
      </div>

      {/* Rules card */}
      <RulesCard rules={rules} />
    </div>
  );
}

function RulesCard({ rules, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!rules?.length) return null;
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5" data-testid="community-rules">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-2 text-left">
        <ShieldAlert className="w-4 h-4 text-amber-400" />
        <span className="text-sm font-semibold text-white">Cohort ground rules</span>
        <span className="text-xs text-slate-500 ml-auto">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <ol className="mt-4 space-y-3 list-decimal list-inside marker:text-amber-400">
          {rules.map((r, i) => (
            <li key={i} className="text-sm text-slate-300">
              <span className="font-medium text-white">{r.title}.</span>{' '}
              <span className="text-slate-400">{r.body}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// =====================================================================
// SINGLE CHANNEL FEED
// =====================================================================
export function PortalCommunityChannel() {
  const { channelSlug } = useParams();
  const { user } = usePortalAuth();
  const [channels, setChannels] = useState([]);
  const [rules, setRules] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');   // ask-jo only: 'all' | 'unanswered'

  const channel = channels.find(c => c.slug === channelSlug);
  const Icon = ICON_OF(channelSlug);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (!channels.length) {
        const ch = await portalApi.get('/community/channels');
        setChannels(ch.data.channels);
        setRules(ch.data.rules);
      }
      const params = { channel: channelSlug };
      if (filter === 'unanswered') params.filter = 'unanswered';
      const { data } = await portalApi.get('/community/posts', { params });
      setPosts(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [channelSlug, filter, channels.length]);

  useEffect(() => { load(); }, [load]);

  const canPost = channel ? (channel.posts !== 'admin' || user?.role === 'admin') : true;

  return (
    <div data-testid={`community-channel-page-${channelSlug}`}>
      <Link to="/portal/community" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-2 mb-4">
        <ArrowLeft className="w-4 h-4" /> All channels
      </Link>

      <header className="mb-6 flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
          <Icon className="w-6 h-6 text-amber-400" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-white">{channel?.name || channelSlug}</h1>
          {channel?.description && <p className="text-slate-400 text-sm mt-1">{channel.description}</p>}
        </div>
      </header>

      {/* Filters for Ask Jo */}
      {channelSlug === 'ask-jo' && (
        <div className="flex gap-2 mb-4">
          <FilterBtn active={filter === 'all'} onClick={() => setFilter('all')} testId="filter-all">All</FilterBtn>
          <FilterBtn active={filter === 'unanswered'} onClick={() => setFilter('unanswered')} testId="filter-unanswered">Unanswered only</FilterBtn>
        </div>
      )}

      {/* Composer */}
      {canPost ? (
        <PostComposer channel={channelSlug} onPosted={load} />
      ) : (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 text-sm text-slate-400 mb-6">
          Only Jo can post in this channel — but watch this space for updates.
        </div>
      )}

      {/* Feed */}
      {loading ? (
        <div className="text-slate-500 text-sm py-8 text-center">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="text-slate-500 text-sm py-12 text-center border border-dashed border-slate-800 rounded-xl">
          {channelSlug === 'ask-jo' && filter === 'unanswered'
            ? 'No unanswered questions. Nice work, Jo.'
            : 'Nothing here yet — be the first to post.'}
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map(p => <PostCard key={p.id} post={p} onChanged={load} />)}
        </div>
      )}

      <div className="mt-8">
        <RulesCard rules={rules} />
      </div>
    </div>
  );
}

function FilterBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-xs px-3 py-1.5 rounded-full border transition
        ${active ? 'bg-amber-500/15 border-amber-500/40 text-amber-300' : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white'}`}
    >
      {children}
    </button>
  );
}

// =====================================================================
// POST COMPOSER
// =====================================================================
function PostComposer({ channel, onPosted }) {
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await portalApi.post('/community/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImageUrl(data.image_url);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim() && !imageUrl) return;
    setPosting(true);
    try {
      await portalApi.post('/community/posts', { channel, content, image_url: imageUrl });
      setContent(''); setImageUrl('');
      onPosted();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPosting(false); }
  };

  const placeholder = {
    introductions: 'Hi! I\'m… here\'s what I\'m building…',
    announcements: 'Share a cohort update…',
    'ask-jo': 'What\'s your technical question?',
    'show-tell': 'Drop your URL and what you\'d like feedback on…',
    wins: 'Celebrate a win, big or small 🏆',
  }[channel] || 'Write a post…';

  return (
    <form onSubmit={submit} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 mb-6" data-testid="post-composer">
      <textarea
        value={content}
        onChange={e => setContent(e.target.value)}
        rows={3}
        maxLength={5000}
        placeholder={placeholder}
        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none resize-y"
        data-testid="composer-content"
      />
      {imageUrl && (
        <div className="relative mt-3 inline-block">
          <img src={`${process.env.REACT_APP_BACKEND_URL}${imageUrl}`} alt="" className="max-h-48 rounded-lg border border-slate-700" />
          <button type="button" onClick={() => setImageUrl('')} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      <div className="flex items-center justify-between mt-3">
        <label className="text-xs cursor-pointer text-slate-400 hover:text-amber-400 inline-flex items-center gap-1.5">
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
          {uploading ? 'Uploading…' : 'Add image'}
          <input type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid="composer-image-input" />
        </label>
        <button
          type="submit"
          disabled={posting || (!content.trim() && !imageUrl)}
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-950 text-sm font-semibold rounded-lg px-4 py-2"
          data-testid="composer-submit"
        >
          {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Post
        </button>
      </div>
    </form>
  );
}

// =====================================================================
// POST CARD (used in feed)
// =====================================================================
function PostCard({ post, onChanged }) {
  const { user } = usePortalAuth();
  const isAdmin = user?.role === 'admin';
  const [liking, setLiking] = useState(false);
  const [likedByMe, setLikedByMe] = useState(post.liked_by_me);
  const [likes, setLikes] = useState(post.likes);

  const toggleLike = async () => {
    if (liking) return;
    setLiking(true);
    try {
      const { data } = await portalApi.post(`/community/posts/${post.id}/like`);
      setLikedByMe(data.liked);
      setLikes(data.likes);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLiking(false); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this post?')) return;
    try { await portalApi.delete(`/community/posts/${post.id}`); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePin = async () => {
    try { await portalApi.patch(`/community/posts/${post.id}/pin`); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleAnswered = async () => {
    try { await portalApi.patch(`/community/posts/${post.id}/answered`); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <article
      className={`rounded-xl border bg-slate-900/60 p-5 ${post.pinned ? 'border-amber-500/40' : 'border-slate-800'}`}
      data-testid={`post-card-${post.id}`}
    >
      <header className="flex items-start gap-3 mb-3">
        <Avatar user={post.author} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/portal/cohort/${post.author?.id}`} className="text-sm font-semibold text-white hover:text-amber-400">
              {post.author?.full_name || 'Anonymous'}
            </Link>
            {post.author?.role === 'admin' && <span className="text-[10px] uppercase bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">Jo</span>}
            {post.pinned && <span className="text-[10px] uppercase bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded inline-flex items-center gap-1"><Pin className="w-3 h-3" /> Pinned</span>}
            {post.answered && <span className="text-[10px] uppercase bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded inline-flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Answered</span>}
          </div>
          <div className="text-xs text-slate-500">{timeAgo(post.created_at)}{post.author?.business ? ` · ${post.author.business}` : ''}</div>
        </div>
        {(post.is_owner || isAdmin) && (
          <div className="flex gap-1 shrink-0">
            {isAdmin && post.channel === 'ask-jo' && (
              <button onClick={toggleAnswered} title="Toggle answered" className="p-1.5 text-slate-400 hover:text-emerald-400" data-testid={`toggle-answered-${post.id}`}>
                <CheckCircle className="w-4 h-4" />
              </button>
            )}
            {isAdmin && (
              <button onClick={togglePin} title="Pin/unpin" className="p-1.5 text-slate-400 hover:text-amber-400" data-testid={`toggle-pin-${post.id}`}>
                <Pin className="w-4 h-4" />
              </button>
            )}
            <button onClick={remove} title="Delete" className="p-1.5 text-slate-400 hover:text-rose-400" data-testid={`delete-post-${post.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </header>

      {post.content && (
        <Link to={`/portal/community/post/${post.id}`} className="block text-slate-200 whitespace-pre-wrap leading-relaxed hover:text-white">
          {post.content}
        </Link>
      )}
      {post.image_url && (
        <Link to={`/portal/community/post/${post.id}`} className="block mt-3">
          <img src={`${process.env.REACT_APP_BACKEND_URL}${post.image_url}`} alt="" className="max-h-96 rounded-lg border border-slate-800" />
        </Link>
      )}

      <footer className="flex items-center gap-5 mt-4 text-sm text-slate-400">
        <button onClick={toggleLike} className="inline-flex items-center gap-1.5 hover:text-rose-400 transition" data-testid={`like-${post.id}`}>
          <Heart className={`w-4 h-4 ${likedByMe ? 'fill-rose-500 text-rose-500' : ''}`} /> {likes}
        </button>
        <Link to={`/portal/community/post/${post.id}`} className="inline-flex items-center gap-1.5 hover:text-amber-400 transition">
          <MessageCircle className="w-4 h-4" /> {post.comment_count}
        </Link>
      </footer>
    </article>
  );
}

// =====================================================================
// SINGLE POST + COMMENTS PAGE
// =====================================================================
export function PortalCommunityPost() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = usePortalAuth();
  const [post, setPost] = useState(null);
  const [channelMeta, setChannelMeta] = useState(null);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await portalApi.get(`/community/posts/${postId}`);
      setPost(data);
      // Fetch channel meta for comment rules
      const ch = await portalApi.get('/community/channels');
      setChannelMeta(ch.data.channels.find(c => c.slug === data.channel));
    } catch (e) { toast.error(formatApiError(e)); }
  }, [postId]);

  useEffect(() => { load(); }, [load]);

  const canComment = (() => {
    if (!channelMeta) return false;
    if (channelMeta.comments === 'none') return false;
    if (channelMeta.comments === 'admin-only') return user?.role === 'admin';
    return true;
  })();

  const submitComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setSending(true);
    try {
      await portalApi.post(`/community/posts/${postId}/comments`, { content: comment });
      setComment('');
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  const deleteComment = async (cid) => {
    if (!window.confirm('Delete this comment?')) return;
    try { await portalApi.delete(`/community/comments/${cid}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!post) return <div className="text-slate-500 text-sm">Loading…</div>;

  return (
    <div data-testid="community-post-detail">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-2 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <PostCard post={post} onChanged={load} />

      {/* Comments */}
      <section className="mt-6">
        <h2 className="text-sm font-semibold text-slate-300 mb-3">
          {post.comments?.length || 0} {(post.comments?.length || 0) === 1 ? 'reply' : 'replies'}
        </h2>

        {channelMeta?.comments === 'none' && (
          <div className="text-sm text-slate-500 rounded-lg border border-slate-800 p-4">
            Comments are off in this channel.
          </div>
        )}
        {channelMeta?.comments === 'admin-only' && user?.role !== 'admin' && (
          <div className="text-sm text-slate-500 rounded-lg border border-slate-800 p-4 mb-4">
            Only Jo can reply in Ask Jo — but you can react with ❤️ above.
          </div>
        )}

        <div className="space-y-3">
          {(post.comments || []).map(c => (
            <div key={c.id} className="flex gap-3 rounded-lg bg-slate-900/40 border border-slate-800 p-3" data-testid={`comment-${c.id}`}>
              <Avatar user={c.author} size="sm" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Link to={`/portal/cohort/${c.author?.id}`} className="text-sm font-medium text-white hover:text-amber-400">
                    {c.author?.full_name || 'Anonymous'}
                  </Link>
                  {c.author?.role === 'admin' && <span className="text-[10px] uppercase bg-amber-500/20 text-amber-400 px-1 py-0.5 rounded">Jo</span>}
                  <span className="text-xs text-slate-500">{timeAgo(c.created_at)}</span>
                  {(c.is_owner || user?.role === 'admin') && (
                    <button onClick={() => deleteComment(c.id)} className="ml-auto text-slate-500 hover:text-rose-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <p className="text-sm text-slate-300 whitespace-pre-wrap mt-1">{c.content}</p>
              </div>
            </div>
          ))}
        </div>

        {canComment && (
          <form onSubmit={submitComment} className="mt-4 flex gap-2" data-testid="comment-composer">
            <Avatar user={user} size="sm" />
            <div className="flex-1">
              <textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                rows={2}
                placeholder="Write a reply…"
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none resize-y"
                data-testid="comment-input"
              />
              <button
                type="submit"
                disabled={sending || !comment.trim()}
                className="mt-2 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-950 text-sm font-semibold rounded-lg px-3 py-1.5"
                data-testid="comment-submit"
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Reply
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
